import React, { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import CardChart from '../Components/CardChart'
import { faStar } from '@fortawesome/pro-solid-svg-icons'
import { Box, Skeleton, Typography } from '@mui/material'

const normalizeAssessmentAreaCharts = (rawData) => {
  const source = Array.isArray(rawData) ? rawData : []

  return source.map((area, index) => {
    const areaName =
      area?.name || area?.assessmentAreaName || area?.assessmentArea?.name || `Assessment Area ${index + 1}`
    const terms = Array.isArray(area?.terms)
      ? area.terms
      : Array.isArray(area?.term)
        ? area.term
        : Array.isArray(area?.items)
          ? area.items
          : Array.isArray(area?.levels)
            ? area.levels
            : Array.isArray(area?.leevels)
              ? area.leevels
              : []

    const chartData = terms.map((term) => {
      const rawValue = term?.currentLevel ? (term?.currentLevel?.levelPosition ?? 0) : 0
      const label = term?.shortName ?? term?.name ?? (typeof term === 'string' ? term : '')
      const levelName = term?.currentLevel?.level || ''
      const value = Number(rawValue)
      const color =
        term?.currentLevel?.levelPrimaryColor ||
        term?.currentLevel?.levelSecondaryColor ||
        term?.currentLevel?.levelColor ||
        '#9DC7EA'

      return {
        label: String(label || 'N/A'),
        value: Number.isFinite(value) ? value : 0,
        levelName: String(levelName || ''),
        color: String(color || '#9DC7EA')
      }
    })

    return {
      name: areaName,
      levels: area.levels,
      chartData
    }
  })
}

const YourGrowthJourney = ({ isLoadingDashboard, isLoadingCurriculum, data = { data: [], title: '' } }) => {
  const assessmentCharts = useMemo(() => normalizeAssessmentAreaCharts(data?.data), [data])

  return (
    <CardChart
      icon={'signal'}
      title='Your Growth Journey'
      bgColor='#fff'
      border='#c3c3c4'
      isLoading={isLoadingDashboard || isLoadingCurriculum}
      px={0}
    >
      {isLoadingDashboard || isLoadingCurriculum ? (
        <Box className='px-3 py-4'>
          <Box className='flex gap-2 items-center justify-center mb-4'>
            <Skeleton width={100} height={20} variant='rounded' />
          </Box>
          <Box className='flex gap-2 items-center w-full justify-evenly'>
            <Box className='flex flex-col gap-2 items-center  h-40 '>
              <Skeleton width={30} height={'100%'} variant='rounded' />
              <Skeleton width={30} height={10} variant='rounded' />
            </Box>
            <Box className='flex flex-col gap-2 items-center  h-40 '>
              <Skeleton width={30} height={'100%'} variant='rounded' />
              <Skeleton width={30} height={10} variant='rounded' />
            </Box>{' '}
            <Box className='flex flex-col gap-2 items-center  h-40 '>
              <Skeleton width={30} height={'100%'} variant='rounded' />
              <Skeleton width={30} height={10} variant='rounded' />
            </Box>{' '}
            <Box className='flex flex-col gap-2 items-center  h-40 '>
              <Skeleton width={30} height={'100%'} variant='rounded' />
              <Skeleton width={30} height={10} variant='rounded' />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          className='px-1 overflow-auto'
        >
          {assessmentCharts.map(({ name, chartData, levels }, index) => {
            const maxValue = Math.max(...chartData.map((item) => Number(item.value) || 0), 1)

            const validPositions = [
              ...levels
                .map((level) => Number(level?.position ?? -1))
                .filter((pos) => Number.isFinite(pos) && pos >= 0)
                .map((pos) => pos + 1),
              maxValue >= 0 ? maxValue + 1 : null
            ].filter((value) => value !== null && Number.isFinite(value))
            const rangeMax = validPositions.length ? Math.max(...validPositions) : 1
            const rangeMin = 0

            const option = {
              backgroundColor: 'transparent',
              tooltip: {
                show: false,
                trigger: 'axis',
                backgroundColor: '#fff',
                borderColor: '#E4E7EC',
                textStyle: { color: '#101828' }
              },
              grid: {
                left: 24,
                right: 20,
                top: 18,
                bottom: 30,
                containLabel: true
              },
              xAxis: {
                type: 'category',
                data: chartData.map((item) => item.label),
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: {
                  color: '#667085',
                  fontSize: 11
                }
              },
              yAxis: {
                type: 'value',
                min: rangeMin,
                max: rangeMax,
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: {
                  show: true,
                  lineStyle: {
                    color: '#E4E7EC',
                    type: 'solid'
                  }
                },
                axisLabel: { show: false }
              },
              series: [
                {
                  type: 'bar',
                  barWidth: 28,
                  data: chartData.map((item) => ({
                    value: item.value,
                    levelName: item.levelName,
                    itemStyle: {
                      color: item.color || '#9DC7EA',
                      borderRadius: [4, 4, 0, 0]
                    },
                    label: {
                      show: !!item.levelName && item.value > 0,
                      position: 'top',
                      color: '#344054',
                      fontSize: 10,
                      fontWeight: 600,
                      formatter: (params) => {
                        if (!params.data.levelName || params.data.value <= 0) {
                          return ''
                        }

                        return params.data.levelName
                      }
                    }
                  })),
                  showBackground: true,
                  backgroundStyle: {
                    color: 'rgba(15, 23, 42, 0.06)',
                    borderRadius: [4, 4, 0, 0]
                  }
                }
              ]
            }

            return (
              <Box
                key={`${name}-${index}`}
                sx={{
                  backgroundColor: index % 2 ? 'rgba(222, 237, 248, 0.5)' : 'none',
                  borderLeft: index > 0 ? '1px solid #E4E7EC' : 'none',
                  width: `calc(100% / ${data.length})`
                }}
                className=' min-w-200px w-full  pt-4'
              >
                <Box sx={{ textAlign: 'center', fontSize: 12, fontWeight: 500, color: '#7c828d' }}>
                  {name}
                </Box>
                <ReactECharts
                  option={option}
                  style={{ height: '150px', width: '100%' }}
                  opts={{ renderer: 'svg' }}
                />
              </Box>
            )
          })}
        </Box>
      )}
      <Typography className='px-4 font-medium' sx={{ color: '#7c828d' }}>
        {isLoadingDashboard || isLoadingCurriculum ? (
          <Skeleton width='150px' />
        ) : (
          data?.title || 'Your Growth Journey'
        )}
      </Typography>
    </CardChart>
  )
}

export default YourGrowthJourney
